import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading } from "../components/kit";
import { Button } from "../components/ui/button";
import { TaskModal } from "../components/actions";
import { useAuth } from "../context/AuthContext";

export default function CalendarPage() {
  const { user } = useAuth();
  const isMaster = user?.role === "master";
  const [tasks, setTasks] = useState(null);
  const [ref, setRef] = useState(new Date());
  const [modal, setModal] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const [t, a] = await Promise.all([api.get("/tasks?limit=500"), api.get("/appointments?limit=500")]);
    const items = [...(t.data.items || []).map((x) => ({ ...x, kind: "task" })), ...(a.data.items || []).filter((x) => x.show_on_calendar !== false).map((x) => ({ ...x, kind: "event", due_date: x.date }))];
    setTasks(items);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!tasks) return <Loading label="Loading calendar" />;

  const year = ref.getFullYear(), month = ref.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthName = ref.toLocaleString("default", { month: "long", year: "numeric" });
  const todayStr = new Date().toISOString().slice(0, 10);

  const eventsFor = (d) => {
    const ds = new Date(year, month, d).toISOString().slice(0, 10);
    return tasks.filter((t) => t.due_date === ds);
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Calendar" subtitle="Follow-ups, showings, closings, and appointments." testid="calendar-header"
        actions={<Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => setModal(true)}><Plus className="w-4 h-4 mr-1.5" />New Task</Button>} />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-display font-bold text-navy-800 text-lg">{monthName}</h3>
          <div className="flex gap-1">
            <button onClick={() => setRef(new Date(year, month - 1, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setRef(new Date())} className="px-3 py-2 rounded-lg hover:bg-slate-100 text-sm">Today</button>
            <button onClick={() => setRef(new Date(year, month + 1, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 border-b border-slate-100">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const ds = d ? new Date(year, month, d).toISOString().slice(0, 10) : null;
            const evs = d ? eventsFor(d) : [];
            return (
              <div key={i} className={`min-h-[110px] border-b border-r border-slate-100 p-1.5 ${ds === todayStr ? "bg-gold/5" : ""}`}>
                {d && <><button onClick={() => isMaster && nav(`/agenda?date=${ds}`)} title={isMaster ? "Open this day in the Daily Planner" : undefined} data-testid={`cal-day-${ds}`} className={`text-xs font-medium ${isMaster ? "hover:ring-2 hover:ring-gold cursor-pointer" : "cursor-default"} ${ds === todayStr ? "bg-gold text-navy-900 rounded-full w-6 h-6 inline-flex items-center justify-center" : "text-slate-500 rounded-full w-6 h-6 inline-flex items-center justify-center"}`}>{d}</button>
                  <div className="space-y-1 mt-1">
                    {evs.slice(0, 3).map((e) => (
                      <div key={e.id} onClick={() => e.contact_id && nav(`/contacts/${e.contact_id}`)} className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer ${e.status === "completed" ? "bg-slate-100 text-slate-400 line-through" : "bg-navy-800 text-white"}`}>{e.title}</div>
                    ))}
                    {evs.length > 3 && <p className="text-[10px] text-slate-400">+{evs.length - 3} more</p>}
                  </div></>}
              </div>
            );
          })}
        </div>
      </div>

      <TaskModal open={modal} onClose={() => { setModal(false); load(); }} onSaved={load} />
    </div>
  );
}
